import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Row, Col, Form, Card, Spinner } from 'react-bootstrap'
import { Typeahead } from 'react-bootstrap-typeahead'
import { FaPrint, FaEye, FaArrowLeft } from 'react-icons/fa'
import {
  BsFunnelFill,
  BsSearch,
  BsArrowClockwise,
  BsFileEarmarkText,
  BsDownload,
} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import ApiEndpoints, { BASE_URL } from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import Cookies from 'js-cookie'
import axios from 'axios'
import html2pdf from 'html2pdf.js'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import { useParams, useLocation, useNavigate } from 'react-router-dom'

import CertificateTemplate1 from '../certificateTemplates/CertificateTemplate1'
import CertificateTemplate2 from '../certificateTemplates/CertificateTemplate2'
import CertificateTemplate3 from '../certificateTemplates/CertificateTemplate3'
import CertificateTemplate4 from '../certificateTemplates/CertificateTemplate4'
import CertificateTemplate5 from '../certificateTemplates/CertificateTemplate5'
import CertificateTemplate6 from '../certificateTemplates/CertificateTemplate6'
import CertificateTemplate7 from '../certificateTemplates/CertificateTemplate7'
import CertificateTemplate8 from '../certificateTemplates/CertificateTemplate8'

import CertificateAchievementPrint, {
  buildCertificateAchievementPreviewData,
} from './CertificateAchievementPrint'
import TransferCertificatePrint, {
  buildTransferCertificatePreviewData,
} from './TransferCertificatePrint'
import ExamMarksheetPrint, { buildExamMarksheetPreviewData } from './ExamMarksheetPrint'

/** Same ids/titles/types as TemplateAssignment → Custom Template section */
const customTemplateRegistry = [
  { id: '9', title: 'Course Completion Certificate', type: 'course_completion_certificate' },
  { id: '10', title: 'Transfer Certificate', type: 'transfer_certificate' },
  { id: '11', title: 'Exam Marksheet', type: 'exam_marksheet' },
]

const templateRegistry = [
  {
    id: '1',
    title: 'Achievement/Merit Certificate',
    type: 'achievement_merit_template',
    key: 'achievement_merit_template',
    component: CertificateTemplate1,
  },
  {
    id: '2',
    title: 'Transfer Certificate',
    type: 'transfer_certificate_template',
    key: 'template2',
    component: CertificateTemplate2,
  },
  {
    id: '3',
    title: 'Character Certificate',
    type: 'character_certificate_template',
    key: 'template3',
    component: CertificateTemplate3,
  },
  {
    id: '4',
    title: 'Vocational/Skill Certificate',
    type: 'vocational_skill_template',
    key: 'template4',
    component: CertificateTemplate4,
  },
  {
    id: '5',
    title: 'Passing Certificate',
    type: 'passing_certificate_template',
    key: 'template5',
    component: CertificateTemplate5,
  },
  {
    id: '6',
    title: 'Migration Certificate',
    type: 'migration_certificate_template',
    key: 'template6',
    component: CertificateTemplate6,
  },
  {
    id: '7',
    title: 'Cultural Activity Certificate',
    type: 'cultural_activity_template',
    key: 'template7',
    component: CertificateTemplate7,
  },
  {
    id: '8',
    title: 'Improvement Certificate',
    type: 'improvement_certificate_template',
    key: 'template8',
    component: CertificateTemplate8,
  },
]

const normalizeTemplateId = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const direct = templateRegistry.find(
      (tpl) =>
        tpl.id === trimmed ||
        tpl.key === trimmed.toLowerCase() ||
        tpl.type === trimmed ||
        tpl.title.toLowerCase() === trimmed.toLowerCase(),
    )
    if (direct) return direct.id
    if (trimmed.startsWith('template')) {
      const numeric = Number(trimmed.replace('template', ''))
      return Number.isNaN(numeric) ? null : String(numeric)
    }
    const numeric = Number(trimmed)
    return Number.isNaN(numeric) ? null : String(numeric)
  }

  if (typeof value === 'number') return String(value)
  return null
}

const getTemplateByAnyValue = (...values) => {
  for (const value of values) {
    const normalizedId = normalizeTemplateId(value)
    const byId = templateRegistry.find((tpl) => tpl.id === normalizedId)
    if (byId) return byId

    if (typeof value === 'string') {
      const lowered = value.toLowerCase()
      const byName = templateRegistry.find(
        (tpl) =>
          tpl.key === lowered ||
          tpl.type === value ||
          tpl.title.toLowerCase() === lowered,
      )
      if (byName) return byName
    }
  }

  return null
}

const resolveTemplateEntry = (...values) => {
  const standard = getTemplateByAnyValue(...values)
  if (standard) return { ...standard, isCustom: false }

  for (const value of values) {
    if (value === null || value === undefined) continue
    const idCandidate =
      typeof value === 'object' && value !== null
        ? value.template_id ?? value.id
        : value
    const normalizedId = normalizeTemplateId(idCandidate)
    const customById = customTemplateRegistry.find((t) => t.id === normalizedId)
    if (customById) return { ...customById, isCustom: true }
  }

  for (const value of values) {
    if (typeof value === 'string') {
      const byType = customTemplateRegistry.find((t) => t.type === value)
      if (byType) return { ...byType, isCustom: true }
    }
    if (typeof value === 'object' && value !== null) {
      const t = value.template_type || value.type
      if (typeof t === 'string') {
        const byType = customTemplateRegistry.find((c) => c.type === t)
        if (byType) return { ...byType, isCustom: true }
      }
    }
  }

  return null
}

const isTruthyActive = (value) =>
  value === true ||
  value === 1 ||
  value === '1' ||
  value === 'true' ||
  value === 'active' ||
  value === 'ACTIVE'

const resolveActiveTemplateFromResponse = (payload, instituteId) => {
  const dataNode = payload?.data ?? payload
  const rows = Array.isArray(dataNode)
    ? dataNode
    : Array.isArray(dataNode?.templates)
      ? dataNode.templates
      : Array.isArray(dataNode?.template_list)
        ? dataNode.template_list
        : []

  const activeTemplateNode =
    dataNode?.active_template ??
    payload?.active_template ??
    dataNode?.activeTemplate ??
    payload?.activeTemplate

  // Case 1: API returns active_template as object/id/name at top-level.
  if (
    activeTemplateNode &&
    (typeof activeTemplateNode === 'string' ||
      typeof activeTemplateNode === 'number' ||
      typeof activeTemplateNode === 'object')
  ) {
    const directTemplate = resolveTemplateEntry(
      activeTemplateNode?.template_id ?? activeTemplateNode?.id,
      activeTemplateNode?.template_name ?? activeTemplateNode?.name,
      activeTemplateNode?.template_type ?? activeTemplateNode?.type,
      activeTemplateNode,
    )

    if (directTemplate) {
      return {
        row:
          typeof activeTemplateNode === 'object' && activeTemplateNode !== null
            ? activeTemplateNode
            : null,
        template: directTemplate,
      }
    }
  }

  // Case 2: API returns rows and one row is marked active_template.
  const instituteRows = rows.filter((row) => {
    const rowInstituteId =
      row?.institute_id ??
      row?.instituteId ??
      row?.assigned_institute_id ??
      row?.assignedInstituteId

    const assignedInstituteList = Array.isArray(row?.institutes)
      ? row.institutes
      : Array.isArray(row?.assigned_institutes)
        ? row.assigned_institutes
        : Array.isArray(row?.assignedInstitutes)
          ? row.assignedInstitutes
          : []

    return (
      Number(rowInstituteId) === instituteId ||
      assignedInstituteList.some((item) => {
        const nestedId = item?.id ?? item?.institute_id ?? item?.instituteId ?? item
        return Number(nestedId) === instituteId
      }) ||
      rows.length === 1
    )
  })

  const activeRow =
    instituteRows.find((row) => isTruthyActive(row?.active_template)) ||
    instituteRows.find((row) => isTruthyActive(row?.is_active)) ||
    instituteRows.find((row) => isTruthyActive(row?.active)) ||
    instituteRows[0] ||
    null

  if (!activeRow) return { row: null, template: null }

  const resolvedTemplate = resolveTemplateEntry(
    activeRow?.template_id,
    activeRow?.template_name,
    activeRow?.template_type,
    activeRow?.id,
    activeRow?.active_template,
  )

  return { row: activeRow, template: resolvedTemplate }
}

const buildCertificateViewDropdownOptions = (resolvedTemplate, activeRow) => {
  const opts = []
  const seen = new Set()
  const add = (value, label) => {
    const v = String(value)
    if (!label || seen.has(v)) return
    seen.add(v)
    opts.push({ value: v, label })
  }
  if (resolvedTemplate) {
    add(
      resolvedTemplate.id,
      activeRow?.template_name || activeRow?.name || resolvedTemplate.title,
    )
  }
  customTemplateRegistry.forEach((c) => {
    add(c.id, c.title)
  })
  return opts
}

const buildActiveTemplateState = (mode, resolvedTemplate, activeRow, viewOpts) => {
  const labelForMode = viewOpts.find((o) => o.value === String(mode))?.label
  const std = templateRegistry.find((t) => t.id === String(mode))
  if (std) {
    return {
      ...std,
      isCustom: false,
      apiRow: activeRow,
      displayName:
        labelForMode || activeRow?.template_name || activeRow?.name || std.title,
    }
  }
  const cus = customTemplateRegistry.find((t) => t.id === String(mode))
  if (cus) {
    return {
      ...cus,
      isCustom: true,
      apiRow: activeRow,
      displayName:
        labelForMode || activeRow?.template_name || activeRow?.name || cus.title,
    }
  }
  return {
    ...resolvedTemplate,
    apiRow: activeRow,
    displayName:
      activeRow?.template_name || activeRow?.name || resolvedTemplate.title,
  }
}

const formatDisplayDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

const TemplateBasedCertificate = () => {
  const { logout, token, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const location = useLocation()
  const navigate = useNavigate()
  const { student_id, course_id, class_id } = useParams()
  const instituteId = Number(Cookies.get('institute_id') || user?.institute_id || 0)

  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name } = useInstitute()
  const [showFilterCard, setShowFilterCard] = useState(true)
  const [courses, setCourses] = useState([])
  const [selectedCourseId, setSelectedCourseId] = useState('')
  const [selectedCourseName, setSelectedCourseName] = useState('')
  const [selectedClass, setSelectedClass] = useState('')
  const [classes, setClasses] = useState([])
  const [allStududents, setAllStudents] = useState([])
  const [students, setStudents] = useState([])
  const [data, setData] = useState([])
  const [selectedStudent, setSelectedStudent] = useState('')
  const [selectedStudentInfo, setSelectedStudentInfo] = useState(null)
  const [showGenerateButton, setShowGenerateButton] = useState(true)
  const [errorMessageDuringGenerate, setErrorMessageDuringGenerate] = useState('')
  const [openCertificate, setOpenCertificate] = useState(false)
  const [certificateData, setCertificateData] = useState(null)
  const [noCertificateFromProfile, setNoCertificateFromProfile] = useState(false)
  const [emptyStateToolbarDismissed, setEmptyStateToolbarDismissed] = useState(false)
  const [toolbarGenerateHiddenByRefresh, setToolbarGenerateHiddenByRefresh] =
    useState(false)
  const [templateLoading, setTemplateLoading] = useState(false)
  const [templateError, setTemplateError] = useState('')
  const [activeTemplate, setActiveTemplate] = useState(null)
  const [viewLoading, setViewLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [certificateViewMode, setCertificateViewMode] = useState('')
  const [activeTemplateDropdownOptions, setActiveTemplateDropdownOptions] = useState([])
  const CERTIFICATE_VIEW_OPTIONS = activeTemplateDropdownOptions
  const certificateViewModeRef = useRef('')

  const hasAutoFetched = useRef(false)
  const certificateRef = useRef(null)

  useEffect(() => {
    certificateViewModeRef.current = certificateViewMode
  }, [certificateViewMode])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    const fetchAllStudent = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'institute-Id': instituteId,
        }
        const response = await axios.get(`${BASE_URL}/student/all`, { headers })
        setAllStudents(response?.data?.data || [])
      } catch (error) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      }
    }

    if (token && instituteId) {
      fetchAllStudent()
    }
  }, [token, instituteId])

  useEffect(() => {
    ApiEndpoints.getAllCourses()
      .then((res) => setCourses(res?.data?.data || []))
      .catch(console.error)
  }, [])

  const fetchAssignedTemplate = useCallback(async () => {
    if (!token || !instituteId) return null

    setTemplateLoading(true)
    setTemplateError('')

    try {
      const response = await axios.get('https://schoolapi.virtualprachar.com/api/template', {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'institute-id': instituteId,
          'institute-Id': instituteId,
        },
      })

      const { row: activeRow, template: resolvedTemplate } =
        resolveActiveTemplateFromResponse(response?.data, instituteId)

      if (!activeRow || !resolvedTemplate) {
        setActiveTemplate(null)
        setActiveTemplateDropdownOptions([])
        setCertificateViewMode('')
        setTemplateError('No template found for this institute.')
        return null
      }

      const viewOpts = buildCertificateViewDropdownOptions(resolvedTemplate, activeRow)
      const valid = new Set(viewOpts.map((o) => o.value))
      const preserved = certificateViewModeRef.current
      const finalMode =
        preserved && valid.has(preserved) ? preserved : String(resolvedTemplate.id)
      const nextTemplate = buildActiveTemplateState(
        finalMode,
        resolvedTemplate,
        activeRow,
        viewOpts,
      )

      setActiveTemplate(nextTemplate)
      setActiveTemplateDropdownOptions(viewOpts)
      setCertificateViewMode(finalMode)
      return nextTemplate
    } catch (error) {
      setActiveTemplate(null)
      setActiveTemplateDropdownOptions([])
      setCertificateViewMode('')
      setTemplateError(
        error?.response?.data?.message || 'Failed to fetch assigned template.',
      )
      return null
    } finally {
      setTemplateLoading(false)
    }
  }, [token, instituteId])

  useEffect(() => {
    fetchAssignedTemplate()
  }, [fetchAssignedTemplate])

  useEffect(() => {
    if (
      allStududents.length > 0 &&
      student_id &&
      course_id &&
      class_id &&
      courses.length > 0
    ) {
      const student = allStududents.find((s) => s.id === Number(student_id))
      if (!student) return

      setSelectedCourseId(Number(course_id))
      setSelectedClass(Number(class_id))
      setSelectedStudent(String(student_id))

      const courseName = courses.find((c) => c.id === Number(course_id))
      if (courseName) {
        setSelectedCourseName(courseName)
      }

      ApiEndpoints.getAllClassesByCourseId(Number(course_id))
        .then((res) => {
          setClasses(res?.data?.data || [])
        })
        .catch((error) => {
          console.error('Error fetching classes:', error)
        })

      axios
        .get(`${BASE_URL}/get/student/by-class?class_id=${class_id}`, {
          headers: {
            'institute-Id': instituteId,
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        })
        .then((res) => {
          const rows = res?.data?.data || []
          setData(rows)
          setStudents(rows)
          setSelectedStudentInfo(
            rows.find((s) => s.id === Number(student_id)) || student,
          )
        })
        .catch(() => {
          setSelectedStudentInfo(student)
        })
    }
  }, [allStududents, student_id, course_id, class_id, courses, instituteId])

  useEffect(() => {
    if (!selectedCourseId) return
    ApiEndpoints.getAllClassesByCourseId(selectedCourseId)
      .then((res) => {
        const responseData = res?.data
        if (responseData?.status === false || !responseData?.data?.length) {
          setClasses([])
          return
        }
        setClasses(responseData.data)
      })
      .catch(() => setClasses([]))
  }, [selectedCourseId])

  const handleCourseChange = async (e) => {
    const courseId = e.target.value
    setSelectedCourseId(courseId)
    setSelectedClass('')
    setSelectedStudent('')
    setStudents([])
    setData([])
    setSelectedStudentInfo(null)
    setOpenCertificate(false)
    setCertificateData(null)
    setErrorMessageDuringGenerate('')
    setShowGenerateButton(true)

    if (courseId) {
      const course = courses.find((c) => c.id === Number(courseId))
      setSelectedCourseName(course || '')
    } else {
      setSelectedCourseName('')
    }

    if (!courseId) {
      setClasses([])
      return
    }

    try {
      const res = await ApiEndpoints.getAllClassesByCourseId(courseId)
      const responseData = res?.data
      if (responseData?.status === false || !responseData?.data?.length) {
        toast.error(responseData?.message || 'No classes found for this course')
        setClasses([])
        return
      }
      setClasses(responseData.data)
    } catch (error) {
      const message = error?.response?.data?.message
      toast.error(message || 'Something went wrong while fetching classes.')
      setClasses([])
    }
  }

  const handleClassChange = async (e) => {
    const classId = e.target.value
    setSelectedClass(classId)
    setSelectedStudent('')
    setOpenCertificate(false)
    setCertificateData(null)
    setSelectedStudentInfo(null)
    setShowGenerateButton(true)

    if (!classId) {
      setStudents([])
      setData([])
      return
    }

    try {
      const res = await axios.get(
        `${BASE_URL}/get/student/by-class?class_id=${classId}`,
        {
          headers: {
            'institute-Id': instituteId,
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        },
      )
      const rows = res?.data?.data || []
      setData(rows)
      setStudents(rows)
    } catch (error) {
      console.error('Error fetching students:', error)
      toast.error('Something went wrong while fetching students.')
      setStudents([])
      setData([])
    }
  }

  const handleStudentChange = (e) => {
    const studentId = e.target.value
    setSelectedStudent(studentId)
    setOpenCertificate(false)
    setCertificateData(null)
    setErrorMessageDuringGenerate('')
    setShowGenerateButton(true)

    const studentInfo = data.find((s) => s.id === Number(studentId)) || null
    const fullInfo =
      allStududents.find((s) => s.id === Number(studentId)) || studentInfo
    setSelectedStudentInfo(studentInfo || fullInfo)
  }

  const handleGenerateCertificate = async () => {
    if (!selectedCourseId || !selectedStudent) {
      toast.error('Please select course and student before generating certificate.')
      return
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/generate-certificate`,
        {
          student_id: selectedStudent,
          course_id: selectedCourseId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      )

      toast.success(response?.data?.message || 'Certificate generated successfully.')
      setShowGenerateButton(false)
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Something went wrong')
    }
  }

  const handleViewCertificate = async () => {
    if (!selectedCourseId || !selectedStudent || !selectedClass) {
      toast.error('Please select course, class, and student first.')
      return
    }

    setViewLoading(true)
    setOpenCertificate(false)
    setTemplateError('')

    try {
      const resolvedTemplate = await fetchAssignedTemplate()
      if (!resolvedTemplate) {
        toast.error('No template found for this institute.')
        return
      }

      const [certificateResponse, marksResponse] = await Promise.all([
        axios.get(
          `${BASE_URL}/certificate?student_id=${selectedStudent}&course_id=${selectedCourseId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        ),
        ApiEndpoints.getStudentExamMarks(selectedStudent, Number(selectedCourseId)),
      ])

      const marksRows = marksResponse?.data?.data || []

      setCertificateData({
        certificate: certificateResponse?.data?.certificate || {},
        student: certificateResponse?.data?.student || selectedStudentInfo || {},
        course: certificateResponse?.data?.course || selectedCourseName || {},
        institute: certificateResponse?.data?.institute || { institute_name: name },
        marks: marksRows,
      })

      setOpenCertificate(true)
      setShowGenerateButton(false)
      setNoCertificateFromProfile(false)
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          'Certificate not found. Please generate first.',
      )
      if (location.state?.fromStudentProfile) {
        setNoCertificateFromProfile(true)
      }
    } finally {
      setViewLoading(false)
    }
  }

  useEffect(() => {
    const autoFetchCertificate = async () => {
      if (
        student_id &&
        course_id &&
        selectedCourseId &&
        selectedStudent &&
        students.length > 0 &&
        !openCertificate &&
        !hasAutoFetched.current
      ) {
        hasAutoFetched.current = true
        try {
          const resolvedTemplate = await fetchAssignedTemplate()
          if (!resolvedTemplate) {
            if (location.state?.fromStudentProfile) {
              setNoCertificateFromProfile(true)
            }
            return
          }

          const [certificateResponse, marksResponse] = await Promise.all([
            axios.get(
              `${BASE_URL}/certificate?student_id=${selectedStudent}&course_id=${selectedCourseId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            ),
            ApiEndpoints.getStudentExamMarks(selectedStudent, Number(selectedCourseId)),
          ])

          const marksRows = marksResponse?.data?.data || []

          setCertificateData({
            certificate: certificateResponse?.data?.certificate || {},
            student: certificateResponse?.data?.student || {},
            course: certificateResponse?.data?.course || {},
            institute: certificateResponse?.data?.institute || {},
            marks: marksRows,
          })
          setOpenCertificate(true)
          setShowGenerateButton(false)
          setNoCertificateFromProfile(false)
        } catch {
          if (location.state?.fromStudentProfile) {
            setNoCertificateFromProfile(true)
          }
        }
      }
    }

    const timer = setTimeout(autoFetchCertificate, 1000)
    return () => clearTimeout(timer)
  }, [
    student_id,
    course_id,
    selectedCourseId,
    selectedStudent,
    students.length,
    openCertificate,
    token,
    location.state,
    fetchAssignedTemplate,
  ])

  useEffect(() => {
    if (openCertificate) setNoCertificateFromProfile(false)
  }, [openCertificate])

  useEffect(() => {
    if (noCertificateFromProfile && !emptyStateToolbarDismissed) {
      setShowFilterCard(false)
    }
  }, [noCertificateFromProfile, emptyStateToolbarDismissed])

  useEffect(() => {
    setEmptyStateToolbarDismissed(false)
    setToolbarGenerateHiddenByRefresh(false)
  }, [student_id, course_id, class_id])

  const handleRefresh = () => {
    setSelectedCourseId('')
    setSelectedClass('')
    setSelectedStudent('')
    setData([])
    setStudents([])
    setOpenCertificate(false)
    setErrorMessageDuringGenerate('')
    setShowGenerateButton(true)
    setSelectedCourseName('')
    setCertificateData(null)
    setSelectedStudentInfo(null)
    setClasses([])
    setTemplateError('')
    if (location.state?.fromStudentProfile) {
      setToolbarGenerateHiddenByRefresh(true)
    }
    fetchAssignedTemplate()
  }

  useEffect(() => {
    const isBaseCertificateRoute =
      location.pathname === '/Certificate' || location.pathname === '/Certificate/'

    if (isBaseCertificateRoute) {
      setSelectedCourseId('')
      setSelectedClass('')
      setSelectedStudent('')
      setData([])
      setStudents([])
      setOpenCertificate(false)
      setErrorMessageDuringGenerate('')
      setShowGenerateButton(true)
      setSelectedCourseName('')
      setCertificateData(null)
      setSelectedStudentInfo(null)
      setClasses([])
      setShowFilterCard(true)
      setNoCertificateFromProfile(false)
      setEmptyStateToolbarDismissed(false)
      setToolbarGenerateHiddenByRefresh(false)
      hasAutoFetched.current = false
      fetchAssignedTemplate()
    }
  }, [location.pathname, location.key, fetchAssignedTemplate])

  const handleDownloadPdf = async () => {
    if (!certificateRef.current || !openCertificate || !activeTemplate) return

    setDownloadLoading(true)
    try {
      const element = certificateRef.current
      const filenameBase =
        certificateData?.student?.name?.replace(/\s+/g, '_') || 'certificate'

      await html2pdf()
        .set({
          margin: [8, 8, 8, 8],
          filename: `${filenameBase}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
          },
          jsPDF: {
            unit: 'mm',
            format: 'a4',
            orientation: 'portrait',
          },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(element)
        .save()
    } catch (error) {
      toast.error('Failed to download PDF.')
    } finally {
      setDownloadLoading(false)
    }
  }

  const fromStudentProfile = !!location.state?.fromStudentProfile
  const isBaseCertificateRoute =
    location.pathname === '/Certificate' || location.pathname === '/Certificate/'
  const showNoCertMessage =
    fromStudentProfile &&
    noCertificateFromProfile &&
    !emptyStateToolbarDismissed
  const showToolbarGenerateButton =
    !isBaseCertificateRoute &&
    !(fromStudentProfile && openCertificate) &&
    !(
      fromStudentProfile &&
      noCertificateFromProfile &&
      emptyStateToolbarDismissed
    ) &&
    !(fromStudentProfile && toolbarGenerateHiddenByRefresh)
  const showGoBackToolbar =
    fromStudentProfile && student_id && !openCertificate

  const handleToolbarGenerateClick = () => {
    setShowFilterCard(true)
    if (fromStudentProfile && noCertificateFromProfile) {
      setEmptyStateToolbarDismissed(true)
    }
  }

  const selectedClassData = classes.find(
    (item) => item.id === Number(selectedClass || ''),
  )

  const templateProps = useMemo(() => {
    const certificate = certificateData?.certificate || {}
    const student = certificateData?.student || selectedStudentInfo || {}
    const course = certificateData?.course || selectedCourseName || {}
    const institute =
      certificateData?.institute || {
        institute_name: name || Cookies.get('institute_name') || '',
      }

    const dateValue =
      certificate?.issue_date ||
      certificate?.date ||
      new Date().toLocaleDateString('en-GB')

    return {
      studentName: student?.name || '',
      courseName: course?.course_name || course?.name || selectedCourseName?.name || '',
      className: selectedClassData?.class_name || '',
      date: formatDisplayDate(dateValue) || dateValue,
      instituteName: institute?.institute_name || name || '',
      certificateTitle: activeTemplate?.title || 'Certificate',
      authorityName: 'Authorized Signatory',
      certificateId:
        certificate?.certificate_number ||
        certificate?.certificate_id ||
        certificate?.id ||
        '',
      logoUrl: logo || '',
      student,
      course,
      classData: selectedClassData,
      certificate,
      institute,
      selectedStudent: student,
      selectedCourse: course,
      selectedClass: selectedClassData,
      activeTemplate,
      certificateViewMode,
      suppressSidebarOffset: true,
    }
  }, [
    certificateData,
    selectedStudentInfo,
    selectedCourseName,
    selectedClassData,
    name,
    activeTemplate,
    logo,
    certificateViewMode,
  ])

  const certificateAchievementPrintPayload = useMemo(() => {
    if (certificateData) {
      return {
        institute: certificateData.institute || { institute_name: name },
        student: certificateData.student || {},
        course: certificateData.course || {},
        certificate: certificateData.certificate || {},
      }
    }
    return buildCertificateAchievementPreviewData(name)
  }, [certificateData, name])

  const transferCertificatePrintPayload = useMemo(() => {
    if (certificateData) {
      const cert = certificateData.certificate || {}
      const student = certificateData.student || {}
      const course = certificateData.course || {}
      const inst = certificateData.institute || {}
      return {
        institute: inst,
        student,
        course,
        tc_number: cert.certificate_number || cert.tc_number,
        tc_issue_date: cert.issue_date || cert.tc_issue_date,
        course_completion_date: cert.course_completion_date || course.end_date,
        reason: cert.reason,
        remarks: cert.remarks,
      }
    }
    return buildTransferCertificatePreviewData(name)
  }, [certificateData, name])

  const examMarksheetPrintPayload = useMemo(() => {
    const rows = certificateData?.marks || certificateData?.certificate?.marksheet_data
    if (Array.isArray(rows) && rows.length > 0) return rows
    return buildExamMarksheetPreviewData()
  }, [certificateData])

  const viewTemplateId = String(certificateViewMode || activeTemplate?.id || '')
  const isCustomCertificateView = ['9', '10', '11'].includes(viewTemplateId)
  const StandardTemplateComponent =
    openCertificate && activeTemplate && !activeTemplate.isCustom
      ? activeTemplate.component
      : null
  const canRenderCertificatePreview =
    openCertificate && (StandardTemplateComponent || isCustomCertificateView)

  const isTransferCertPrintLayout = openCertificate && viewTemplateId === '10'
  const isExamMarksheetPrintLayout = openCertificate && viewTemplateId === '11'

  const handleCertificateViewModeChange = (e) => {
    const v = e.target.value
    setCertificateViewMode(v)
    const std = templateRegistry.find((t) => t.id === v)
    if (std) {
      setActiveTemplate((prev) => ({
        ...std,
        isCustom: false,
        apiRow: prev?.apiRow,
        displayName: std.title,
      }))
      return
    }
    const cus = customTemplateRegistry.find((t) => t.id === v)
    if (cus) {
      setActiveTemplate((prev) => ({
        ...cus,
        isCustom: true,
        apiRow: prev?.apiRow,
        displayName: cus.title,
      }))
    }
  }

  return (
    <>
      <style>
        {`
          .filter-card-container {
            overflow: visible !important;
            position: relative !important;
            z-index: 10 !important;
          }

          .rbt-menu {
            z-index: 1051 !important;
          }

          .template-preview-shell {
            max-width: 1200px;
            margin: 0 auto 32px;
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
          }

          .template-preview-toolbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            padding: 16px 20px;
            border-bottom: 1px solid #e5e7eb;
            background: #f8fafc;
          }

          .template-preview-surface {
            padding: 20px;
            background: #eef2f7;
            width: 100%;
            box-sizing: border-box;
          }

          .template-preview-surface > div {
            width: 100%;
            max-width: 100%;
          }

          .template-preview-surface [class*="actions"]:not([class^="certificate-template-"]) {
            display: none !important;
          }

          .template-empty-state {
            padding: 56px 20px;
            text-align: center;
            color: #6b7280;
            font-size: 14px;
            background: #ffffff;
          }

          @media print {
            .no-print {
              display: none !important;
              visibility: hidden !important;
            }

            body {
              background: #ffffff !important;
            }

            .template-preview-shell {
              border: none !important;
              box-shadow: none !important;
              margin: 0 !important;
              max-width: none !important;
            }

            .template-preview-surface {
              padding: 0 !important;
              background: #ffffff !important;
            }

            /* Transfer Certificate (template 10): match /TransferCertificate/:id print layout */
            [data-tc-template-print="true"] .template-tc-print-preview-wrap {
              padding-left: 0 !important;
              padding-right: 0 !important;
              padding-bottom: 0 !important;
            }
            [data-tc-template-print="true"] .template-preview-shell {
              overflow: visible !important;
              border-radius: 0 !important;
            }
            [data-tc-template-print="true"] .transfer-cert-print-wrap {
              max-width: 100% !important;
            }

            /* Exam Marksheet (template 11): match /ExamMarksheet print width
               NOTE: data-em-template-print lives on the SAME node as .template-based-certificate-root
               (must use compound selector, not descendant). */
            body:has(.template-based-certificate-root[data-em-template-print="true"]) .App {
              display: block !important;
              width: 100% !important;
              max-width: 100% !important;
            }
            body:has(.template-based-certificate-root[data-em-template-print="true"]) .app-main-content {
              width: 100% !important;
              max-width: 100% !important;
              min-width: 0 !important;
              flex: none !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            .template-based-certificate-root[data-em-template-print="true"] {
              margin-left: 0 !important;
              margin-right: 0 !important;
              padding-left: 0 !important;
              padding-right: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
            }
            [data-em-template-print="true"] .template-tc-print-preview-wrap {
              padding-left: 0 !important;
              padding-right: 0 !important;
              padding-bottom: 0 !important;
            }
            [data-em-template-print="true"] .template-preview-shell {
              overflow: visible !important;
              border-radius: 0 !important;
              border: none !important;
              box-shadow: none !important;
              margin: 0 !important;
              max-width: none !important;
              width: 100% !important;
            }
            [data-em-template-print="true"] .template-preview-surface {
              padding: 0 !important;
              background: #ffffff !important;
              width: 100% !important;
            }
            [data-em-template-print="true"] .template-preview-surface > div {
              width: 100% !important;
              max-width: 100% !important;
            }
            [data-em-template-print="true"] .exam-marksheet-print-root {
              width: 100% !important;
              max-width: 100% !important;
            }
            [data-em-template-print="true"] .watermark-content {
              width: 100% !important;
              max-width: 100% !important;
            }
            [data-em-template-print="true"] .exam-marksheet-print-root .print-card {
              width: 100% !important;
              max-width: 100% !important;
              left: 0 !important;
              position: relative !important;
              margin: 0 !important;
              border-radius: 0 !important;
              box-shadow: none !important;
              box-sizing: border-box !important;
            }
          }
        `}
      </style>

      <div
        className={[
          'template-based-certificate-root',
          isTransferCertPrintLayout ? 'tc-print-root' : '',
          isExamMarksheetPrintLayout ? 'em-print-root' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-tc-template-print={isTransferCertPrintLayout ? 'true' : undefined}
        data-em-template-print={isExamMarksheetPrintLayout ? 'true' : undefined}
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        <div
          className="container-fluid p-0 no-print"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <div className="d-flex flex-column">
                  <h3
                    className="m-0 fw-bold transfer-page-title text-start"
                    style={{ fontSize: isMobile ? '20px' : undefined }}
                  >
                    Generate Template-Based Certificates
                  </h3>
                  <p
                    className="m-0 mt-0 text-start"
                    style={{
                      fontSize: isMobile ? '12px' : '13px',
                      color: '#6b7280',
                    }}
                  >
                    View certificate preview using the template assigned to this institute.
                  </p>
                </div>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div className="no-print">
          <div
            className="container-fluid p-0 mt-4"
            style={{
              paddingLeft: isCollapsed ? '32px' : '40px',
              paddingRight: '32px',
              paddingTop: '32px',
              paddingBottom: '20px',
            }}
          >
            {showNoCertMessage && (
              <div
                className="mb-4 text-center"
                style={{
                  padding: '24px',
                  fontSize: '14px',
                  color: '#6b7280',
                }}
              >
                No certificate is available
              </div>
            )}

            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
              <div className="d-flex flex-wrap align-items-center gap-3">
                {(!noCertificateFromProfile || showFilterCard) && (
                  <button
                    onClick={() => setShowFilterCard(!showFilterCard)}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                      border: '1px solid #D1D3D4',
                      color: showFilterCard ? '#ffffff' : '#223843',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
                  >
                    <BsFunnelFill size={20} />
                  </button>
                )}

                {showGoBackToolbar && (
                  <button
                    type="button"
                    onClick={() => navigate(`/student/profile/${student_id}`)}
                    style={{
                      borderRadius: '10px',
                      padding: '10px 14px',
                      border: '1px solid #10B981',
                      backgroundColor: '#10B981',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <FaArrowLeft size={18} />
                    <span>Go Back</span>
                  </button>
                )}
              </div>

              {showToolbarGenerateButton && (
                <button
                  type="button"
                  // onClick={handleToolbarGenerateClick}
                  onClick={handleGenerateCertificate}
                  style={{
                    borderRadius: '10px',
                    padding: '6px 10px',
                    height: '44px',
                    border: 'none',
                    backgroundColor: '#223843',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                  title="Generate Certificate"
                >
                  <BsFileEarmarkText size={18} />
                  <span style={{ fontSize: '12px', fontWeight: '650' }}>
                    Generate Certificate
                  </span>
                </button>
              )}
            </div>

            <div
              className="filter-card-container"
              style={{
                maxHeight: showFilterCard ? '1000px' : '0',
                overflow: showFilterCard ? 'visible' : 'hidden',
                transition: 'max-height 0.3s ease-in-out',
                marginBottom: showFilterCard ? '24px' : '0',
                position: 'relative',
                zIndex: showFilterCard ? 10 : 1,
              }}
            >
              {showFilterCard && (
                <Card
                  style={{
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    overflow: 'visible',
                  }}
                >
                  <div
                    style={{
                      background: '#ffffff',
                      padding: '20px',
                      borderBottom: '1px solid #F3F4F6',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#223843',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                        </div>
                        <div>
                          <h5
                            className="m-0 fw-semibold text-start"
                            style={{ color: '#111827', fontSize: '16px' }}
                          >
                            Search & Filter Certificate
                          </h5>
                          <p
                            className="m-0 mt-1"
                            style={{ fontSize: '13px', color: '#6b7280' }}
                          >
                            Select course, class, and student to load the active template preview.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>

                  <Card.Body
                    className="p-4"
                    style={{
                      backgroundColor: '#ffffff',
                      overflow: 'visible',
                    }}
                  >
                    <Row className="g-4 text-start">
                      <Col xs={12} md={4}>
                        <Form.Group className="text-start fw-bold mb-0">
                          <Form.Label
                            style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}
                          >
                            Select Course
                          </Form.Label>
                          <Typeahead
                            id="course-select"
                            labelKey="name"
                            onChange={(selected) => {
                              const course = selected[0]
                              handleCourseChange({ target: { value: course?.id || '' } })
                            }}
                            options={courses}
                            placeholder="Select Course"
                            selected={courses.filter(
                              (cs) => cs.id === Number(selectedCourseId || ''),
                            )}
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={4}>
                        <Form.Group className="text-start fw-bold mb-0">
                          <Form.Label
                            style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}
                          >
                            Select Class
                          </Form.Label>
                          <Typeahead
                            id="class-select"
                            labelKey="class_name"
                            onChange={(selected) => {
                              const cls = selected[0]
                              handleClassChange({ target: { value: cls?.id || '' } })
                            }}
                            options={classes}
                            placeholder="Select Class"
                            selected={classes.filter(
                              (cs) => cs.id === Number(selectedClass || ''),
                            )}
                          />
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={4}>
                        <Form.Group className="text-start fw-bold">
                          <Form.Label
                            style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}
                          >
                            Select Student
                          </Form.Label>
                          <Typeahead
                            id="student-select"
                            labelKey={(option) =>
                              `${option.name} - ${option.admission_number}`
                            }
                            onChange={(selected) => {
                              const student = selected[0]
                              handleStudentChange({ target: { value: student?.id || '' } })
                            }}
                            options={students}
                            placeholder="Select Student"
                            selected={
                              Array.isArray(students)
                                ? students.filter(
                                    (s) => s.id === Number(selectedStudent || ''),
                                  )
                                : []
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {selectedCourseId && selectedClass && selectedStudent && (
                      <Row className="mt-4">
                        <Col xs={12}>
                          <div className="d-flex align-items-center gap-3 flex-wrap">
                            {/* {showGenerateButton && (
                              <button
                                onClick={handleGenerateCertificate}
                                style={{
                                  borderRadius: '10px',
                                  padding: '10px 14px',
                                  border: 'none',
                                  backgroundColor: '#10B981',
                                  color: '#ffffff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <BsFileEarmarkText size={18} />
                                <span>Generate Certificate</span>
                              </button>
                            )} */}

                            <Form.Group
                              className="mb-0 d-flex align-items-center gap-2"
                              style={{ minWidth: '200px' }}
                            >
                              <Form.Label
                                className="mb-0 text-nowrap fw-bold"
                                style={{ fontSize: '13px', color: '#6b7280' }}
                                htmlFor="certificate-view-mode"
                              >
                                Active templates
                              </Form.Label>
                              <Form.Select
                                id="certificate-view-mode"
                                size="sm"
                                value={certificateViewMode}
                                onChange={handleCertificateViewModeChange}
                                disabled={
                                  templateLoading || !CERTIFICATE_VIEW_OPTIONS.length
                                }
                                style={{
                                  borderRadius: '10px',
                                  maxWidth: '280px',
                                }}
                              >
                                {templateLoading && !CERTIFICATE_VIEW_OPTIONS.length ? (
                                  <option value="">Loading template...</option>
                                ) : CERTIFICATE_VIEW_OPTIONS.length ? (
                                  CERTIFICATE_VIEW_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))
                                ) : (
                                  <option value="">No active template</option>
                                )}
                              </Form.Select>
                            </Form.Group>

                            <button
                              onClick={handleViewCertificate}
                              disabled={viewLoading || templateLoading}
                              style={{
                                borderRadius: '10px',
                                padding: '10px 14px',
                                border: 'none',
                                backgroundColor: '#223843',
                                color: '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: viewLoading || templateLoading ? 0.75 : 1,
                              }}
                            >
                              {viewLoading || templateLoading ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <FaEye size={18} />
                              )}
                              <span>View Certificate</span>
                            </button>

                            <button
                              onClick={() => window.print()}
                              disabled={!canRenderCertificatePreview}
                              style={{
                                borderRadius: '10px',
                                padding: '10px 14px',
                                border: '1px solid #223843',
                                backgroundColor: '#ffffff',
                                color: '#223843',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity: !canRenderCertificatePreview ? 0.5 : 1,
                              }}
                            >
                              <FaPrint size={18} />
                              <span>Print Certificate</span>
                            </button>

                            <button
                              onClick={handleDownloadPdf}
                              disabled={
                                !canRenderCertificatePreview || downloadLoading
                              }
                              style={{
                                borderRadius: '10px',
                                padding: '10px 14px',
                                border: '1px solid #223843',
                                backgroundColor: '#ffffff',
                                color: '#223843',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                opacity:
                                  !canRenderCertificatePreview || downloadLoading
                                    ? 0.5
                                    : 1,
                              }}
                            >
                              {downloadLoading ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <BsDownload size={18} />
                              )}
                              <span>Download PDF</span>
                            </button>
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              )}
            </div>
          </div>
        </div>

        <div
          className="template-tc-print-preview-wrap"
          style={{
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingBottom: '32px',
          }}
        >
          <div className="template-preview-shell">
            <div className="template-preview-toolbar no-print">
              <div className="text-start">
                <div style={{ fontSize: '13px', color: '#6b7280' }}>Active Template</div>
                <div style={{ fontWeight: 700, color: '#111827' }}>
                  {activeTemplate?.displayName || 'Not assigned'}
                </div>
              </div>

              <button
                type="button"
                onClick={fetchAssignedTemplate}
                style={{
                  borderRadius: '10px',
                  border: '1px solid #D1D5DB',
                  background: '#ffffff',
                  color: '#111827',
                  padding: '8px 14px',
                }}
              >
                Refresh Template
              </button>
            </div>

            {templateLoading && !openCertificate ? (
              <div className="template-empty-state">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading assigned template...
              </div>
            ) : templateError && !openCertificate ? (
              <div className="template-empty-state">{templateError}</div>
            ) : canRenderCertificatePreview ? (
              <div className="template-preview-surface">
                <div ref={certificateRef}>
                  {StandardTemplateComponent ? (
                    <StandardTemplateComponent {...templateProps} />
                  ) : viewTemplateId === '9' ? (
                    <CertificateAchievementPrint
                      logo={logo}
                      certificateData={certificateAchievementPrintPayload}
                    />
                  ) : viewTemplateId === '10' ? (
                    <div className="container-fluid px-2 px-md-4 pb-4" style={{ maxWidth: 920 }}>
                      <TransferCertificatePrint
                        logo={logo}
                        instituteName={templateProps.instituteName}
                        data={transferCertificatePrintPayload}
                      />
                    </div>
                  ) : viewTemplateId === '11' ? (
                    <ExamMarksheetPrint
                      logo={logo}
                      instituteName={templateProps.instituteName}
                      data={examMarksheetPrintPayload}
                    />
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="template-empty-state">
                Select course, class, and student, then click `View Certificate` to preview.
              </div>
            )}
          </div>

          {errorMessageDuringGenerate && !openCertificate && (
            <p className="text-danger fw-semibold mt-3">
              {`There is no certificate available for this student. ${errorMessageDuringGenerate}`}
            </p>
          )}
        </div>
      </div>
    </>
  )
}

export default TemplateBasedCertificate


